/**
 * Click-to-walk routes over the squares the party knows: the cheapest path by PF2e movement
 * (every second diagonal costs double), around known hazards when there's a reasonable way past,
 * through allies (you trade places) and creatures that aren't hostile (outside an encounter they step
 * aside: systems/step-aside.ts) but not foes, and as straight as the costs allow. Bot companions
 * (tools/bot.ts) walk the same routes.
 */
import type { CreatureView, DungeonView } from '../shared/view.ts';

export interface Point {
  x: number;
  y: number;
}

const DIRS: readonly [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
/** Extra cost of a hazard square (pit, water, slick floor, web…): worth a detour of up to this many squares. */
const HAZARD_COST = 8;
/** A closed door costs a square to open. */
const DOOR_COST = 1;
/** Breaks ties between equally cheap routes in favour of the one with fewer turns. */
const TURN_COST = 0.001;
/** Extra cost of walking into a creature that isn't hostile (it steps aside): routes go round it when that's no longer. */
const MAKE_WAY_COST = 3;
/** Extra cost of walking into one that can't get out of the way by itself (the game moves it only out of the only way on). */
const STANDS_FAST_COST = 20;
/** Terrain you can't walk into at all (rubble, a locked door). */
const SOLID_TERRAIN = new Set(['%', 'L']);

export interface PathOptions {
  /** Squares (y * width + x) to keep out of, such as one where a step was just refused. */
  avoid?: ReadonlySet<number>;
  /** Route around allies too, instead of trading places with them (the goal square excepted). */
  avoidAllies?: boolean;
  /** Route around creatures that aren't hostile too, instead of walking into them for them to step aside. */
  avoidNeutrals?: boolean;
  /**
   * A found trap's squares are only costly, not shut (bots: tools/bot.ts disarms the trap, opens a trapped door on
   * purpose, or steps on, rather than stand behind it for good). A player's click-to-walk leaves this off.
   */
  throughTraps?: boolean;
}

/**
 * A creature that is neither with the party nor hostile: outside an encounter, it makes way for a
 * character walking into it (systems/step-aside.ts), unless it holds the way (a tollkeeper, a fixed thing).
 */
export function makesWay(c: CreatureView): boolean {
  return c.faction === 'neutral' && c.health !== 'dead' && !c.holdsWay;
}

/** One that makes way but can't move by itself (knocked out, held): walked into, it's moved only when it stands in the only way on. */
function standsFast(c: CreatureView): boolean {
  return c.health === 'down' || !!c.standsFast;
}

/** A small binary heap of states by priority. */
class Heap {
  private readonly items: { key: number; f: number }[] = [];
  get size(): number {
    return this.items.length;
  }
  push(key: number, f: number): void {
    const a = this.items;
    a.push({ key, f });
    let i = a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (a[p].f <= a[i].f) break;
      [a[p], a[i]] = [a[i], a[p]];
      i = p;
    }
  }
  pop(): number {
    const a = this.items;
    const top = a[0];
    const last = a.pop()!;
    if (a.length) {
      a[0] = last;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1, r = l + 1;
        let m = i;
        if (l < a.length && a[l].f < a[m].f) m = l;
        if (r < a.length && a[r].f < a[m].f) m = r;
        if (m === i) break;
        [a[m], a[i]] = [a[i], a[m]];
        i = m;
      }
    }
    return top.key;
  }
}

/**
 * The route from `from` to `to` (excluding the starting square), or null if there's none. States
 * track how many diagonals the route has used (odd or even), since that sets the next diagonal's cost.
 */
export function findPath(d: DungeonView, from: Point, to: Point, opts: PathOptions = {}): Point[] | null {
  const W = d.width, H = d.height;
  if (from.x === to.x && from.y === to.y) return [];
  const inside = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H;
  const tile = (x: number, y: number) => d.tiles[y * W + x];
  const walkable = (x: number, y: number) => {
    if (!inside(x, y)) return false;
    const t = tile(x, y);
    return (t === '.' || t === "'" || t === '+') && !SOLID_TERRAIN.has(d.terrain[y * W + x]);
  };
  const solidFeature = new Set(d.features.filter((f) => f.solid).map((f) => f.y * W + f.x));
  // As in the game: walls, closed doors, solid features and solid terrain stop a diagonal squeezing past.
  const blocksCorner = (x: number, y: number) => {
    if (!inside(x, y)) return true;
    const t = tile(x, y), i = y * W + x;
    return (t !== '.' && t !== "'") || SOLID_TERRAIN.has(d.terrain[i]) || solidFeature.has(i);
  };
  // Foes (and fallen allies, who can't trade places) block; allies you can walk through, unless told to go
  // around, and creatures that aren't hostile step aside (at a cost, so a way round them is taken when there's one).
  const blocked = new Set<number>();
  const makingWay = new Map<number, number>();
  for (const c of d.creatures) {
    if (c.isYou || c.health === 'dead') continue;
    const passable = c.faction === 'party' && c.health !== 'down' && !opts.avoidAllies;
    if (passable) continue;
    const through = makesWay(c) && !opts.avoidNeutrals;
    const s = c.size ?? 1;
    for (let dy = 0; dy < s; dy++) {
      for (let dx = 0; dx < s; dx++) {
        const i = (c.y + dy) * W + c.x + dx;
        if (through) makingWay.set(i, standsFast(c) ? STANDS_FAST_COST : MAKE_WAY_COST);
        else blocked.add(i);
      }
    }
  }
  // Squares that would set off a hazard the party has noticed (a trapped door's leaves among them): a walk
  // never crosses one on its own, only when it is the square clicked, so stepping on a found trap is on purpose.
  const trapped = new Set((d.hazards ?? []).filter((h) => h.live).flatMap((h) => h.area));
  const goal = to.y * W + to.x;
  const open = (x: number, y: number) => {
    const i = y * W + x;
    return walkable(x, y) && !solidFeature.has(i) && !opts.avoid?.has(i) && ((!blocked.has(i) && (opts.throughTraps || !trapped.has(i))) || i === goal);
  };
  if (!walkable(to.x, to.y)) return null;

  // State key: (square * 2 + diagonal parity) * 9 + direction of arrival (8 = none, the start).
  const key = (sq: number, parity: number, dir: number) => (sq * 2 + parity) * 9 + dir;
  const heuristic = (x: number, y: number) => {
    const dx = Math.abs(x - to.x), dy = Math.abs(y - to.y);
    return Math.max(dx, dy) + Math.floor(Math.min(dx, dy) / 2);
  };
  const best = new Map<number, number>();
  const prev = new Map<number, number>();
  const heap = new Heap();
  const start = key(from.y * W + from.x, 0, 8);
  best.set(start, 0);
  heap.push(start, heuristic(from.x, from.y));
  let found = -1;
  for (let guard = 0; heap.size && guard < 200000; guard++) {
    const cur = heap.pop();
    const dir = cur % 9, parity = Math.floor(cur / 9) % 2, sq = Math.floor(cur / 18);
    const g = best.get(cur)!;
    if (sq === goal) {
      found = cur;
      break;
    }
    const x = sq % W, y = (sq - x) / W;
    for (let k = 0; k < 8; k++) {
      const [dx, dy] = DIRS[k];
      const nx = x + dx, ny = y + dy;
      if (!inside(nx, ny) || !open(nx, ny)) continue;
      const diagonal = dx !== 0 && dy !== 0;
      // No squeezing between two blocked squares.
      if (diagonal && blocksCorner(x + dx, y) && blocksCorner(x, y + dy)) continue;
      const ni = ny * W + nx;
      let step = diagonal ? (parity ? 2 : 1) : 1;
      if (tile(nx, ny) === '+') step += DOOR_COST;
      step += makingWay.get(ni) ?? 0;
      const hazard = d.terrain[ni];
      if (((hazard && hazard !== ' ') || trapped.has(ni)) && ni !== goal) step += HAZARD_COST;
      if (dir !== 8 && dir !== k) step += TURN_COST;
      const next = key(ni, diagonal ? 1 - parity : parity, k);
      const cost = g + step;
      if (cost >= (best.get(next) ?? Infinity)) continue;
      best.set(next, cost);
      prev.set(next, cur);
      heap.push(next, cost + heuristic(nx, ny));
    }
  }
  if (found < 0) return null;
  const path: Point[] = [];
  for (let s = found; s !== start; s = prev.get(s)!) {
    const sq = Math.floor(s / 18);
    path.push({ x: sq % W, y: Math.floor(sq / W) });
  }
  return path.reverse();
}

/** Can you step onto this square right now (nothing has moved into the way; someone who makes way doesn't count)? */
export function stepOpen(d: DungeonView, p: Point): boolean {
  const t = d.tiles[p.y * d.width + p.x];
  if (t !== '.' && t !== "'" && t !== '+') return false;
  if (SOLID_TERRAIN.has(d.terrain[p.y * d.width + p.x])) return false;
  if (d.features.some((f) => f.solid && f.x === p.x && f.y === p.y)) return false;
  return !d.creatures.some((c) => {
    if (c.isYou || c.health === 'dead') return false;
    if ((c.faction === 'party' && c.health !== 'down') || makesWay(c)) return false;
    const s = c.size ?? 1;
    return p.x >= c.x && p.y >= c.y && p.x < c.x + s && p.y < c.y + s;
  });
}

/** How long a sent step has to show up before it's sent again; one that doesn't show up twice was refused. */
export const STEP_SHOWS_UP_MS = 700;

/** A click-to-walk under way: the route, the step last sent, and the squares that refused one. */
export interface Walk {
  goal: Point;
  path: Point[];
  sentFrom: Point | null;
  sentTo: Point | null;
  sentAt: number;
  /** Times the step to `sentTo` was sent from `sentFrom` without showing up. */
  tries: number;
  /** Squares (by index) that refused a step on this walk: routes go round them. */
  refused: Set<number>;
}

export function newWalk(d: DungeonView, from: Point, goal: Point): Walk {
  return { goal: { ...goal }, path: findPath(d, from, goal) ?? [], sentFrom: null, sentTo: null, sentAt: 0, tries: 0, refused: new Set() };
}

/**
 * The next step of a walk from `you` (and marks it sent), 'wait' while the last one is on its way, or
 * null when there's no way on. A step that doesn't show up is sent once more (the move cooldown, a
 * slow connection); one that still doesn't was refused (someone who won't make way, something the map
 * doesn't show), and the walk goes round that square from then on instead of walking into it again.
 */
export function walkStep(d: DungeonView, you: Point, walk: Walk, now: number): Point | 'wait' | null {
  const same = (a: Point | null, b: Point) => !!a && a.x === b.x && a.y === b.y;
  if (same(walk.sentFrom, you)) {
    if (now - walk.sentAt < STEP_SHOWS_UP_MS) return 'wait';
    if (walk.sentTo && walk.tries >= 2) {
      walk.refused.add(walk.sentTo.y * d.width + walk.sentTo.x);
      walk.path = [];
    }
  } else {
    walk.tries = 0;
  }
  const reached = walk.path.findIndex((p) => p.x === you.x && p.y === you.y);
  if (reached >= 0) walk.path = walk.path.slice(reached + 1);
  let next = walk.path[0];
  if (!next || Math.max(Math.abs(next.x - you.x), Math.abs(next.y - you.y)) !== 1 || !stepOpen(d, next) || walk.refused.has(next.y * d.width + next.x)) {
    walk.path = findPath(d, you, walk.goal, { avoid: walk.refused }) ?? [];
    next = walk.path[0];
  }
  if (!next) return null;
  if (!same(walk.sentTo, next) || !same(walk.sentFrom, you)) walk.tries = 0;
  walk.tries++;
  walk.sentFrom = { x: you.x, y: you.y };
  walk.sentTo = { ...next };
  walk.sentAt = now;
  return next;
}
